import React, { useState, useEffect, useCallback } from 'react';

const mono = { fontFamily: "'DM Mono', monospace" };
const sans = { fontFamily: "'DM Sans', sans-serif" };
const serif = { fontFamily: "'Cormorant Garamond', serif" };
const label = { ...mono, fontSize: 7.5, letterSpacing: '0.22em', textTransform: 'uppercase', color: '#7A5230', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 };
const CL = () => <span style={{ width: 20, height: 1, background: '#7A5230', flexShrink: 0 }} />;

const card = {
  background: '#FFFFFF', border: '1px solid rgba(7,25,42,0.10)',
  padding: '20px 24px', marginBottom: 24,
};

const statCard = {
  ...card, display: 'flex', flexDirection: 'column', alignItems: 'center',
  justifyContent: 'center', padding: '24px 20px', marginBottom: 0,
};

const th = {
  ...mono, fontSize: 7.5, letterSpacing: '0.16em', textTransform: 'uppercase',
  color: '#7A94A8', padding: '8px 12px', textAlign: 'left',
  borderBottom: '1px solid rgba(7,25,42,0.10)', whiteSpace: 'nowrap',
};

const td = {
  ...sans, fontSize: 14, color: '#4A6278', padding: '10px 12px',
  borderBottom: '1px solid rgba(7,25,42,0.06)', verticalAlign: 'middle',
};

function fmt(n) {
  if (!n && n !== 0) return '--';
  const num = parseFloat(n);
  if (isNaN(num)) return '--';
  return `$${num.toLocaleString()}`;
}

function StatusBadge({ status }) {
  const colors = {
    active: { bg: 'rgba(42,95,63,0.10)', fg: '#2A5F3F' },
    in_progress: { bg: 'rgba(184,134,46,0.10)', fg: '#B8862E' },
    blocked: { bg: 'rgba(122,31,46,0.10)', fg: '#7A1F2E' },
    complete: { bg: 'rgba(10,24,40,0.06)', fg: 'rgba(10,24,40,0.5)' },
    received: { bg: 'rgba(42,95,63,0.10)', fg: '#2A5F3F' },
    pending: { bg: 'rgba(184,134,46,0.10)', fg: '#B8862E' },
    ok: { bg: 'rgba(42,95,63,0.10)', fg: '#2A5F3F' },
    error: { bg: 'rgba(122,31,46,0.10)', fg: '#7A1F2E' },
    not_configured: { bg: 'rgba(184,134,46,0.10)', fg: '#B8862E' },
  };
  const c = colors[status] || colors.active;
  return (
    <span style={{ ...mono, fontSize: 8, letterSpacing: '0.08em', padding: '3px 8px',
      borderRadius: 0, background: c.bg, color: c.fg, textTransform: 'uppercase' }}>
      {(status || '').replace(/_/g, ' ')}
    </span>
  );
}

export default function Admin() {
  const [data, setData] = useState(null);
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortCol, setSortCol] = useState('health_score');
  const [sortAsc, setSortAsc] = useState(true);

  const loadAll = useCallback(() => {
    setError(null);
    setLoading(true);
    Promise.all([
      fetch('/api/admin/dashboard', { credentials: 'include' })
        .then(r => { if (!r.ok) throw new Error(`Server returned ${r.status}`); return r.json(); }),
      fetch('/api/prospects', { credentials: 'include' })
        .then(r => r.ok ? r.json() : []),
    ]).then(([dashboard, pros]) => {
      setData(dashboard);
      setProspects(Array.isArray(pros) ? pros : []);
      setLoading(false);
    }).catch(err => { setError(err.message); setLoading(false); });
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  function approveProspect(id) {
    fetch(`/api/prospect/${id}/approve`, { method: 'PATCH', credentials: 'include' })
      .then(r => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(() => { setProspects(prev => prev.map(p => p.id === id ? { ...p, status: 'approved' } : p)); })
      .catch(err => alert('Approve failed: ' + err.message));
  }

  if (loading) {
    return (
      <div style={{ padding: 60, textAlign: 'center' }}>
        <div style={{ ...mono, fontSize: 10, color: '#7A5230', letterSpacing: '0.14em' }}>LOADING...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 60, textAlign: 'center' }}>
        <div style={{ ...mono, fontSize: 11, color: '#7A1F2E', marginBottom: 16 }}>{error}</div>
        <button onClick={loadAll} style={{ ...mono, fontSize: 9, letterSpacing: '0.14em',
          padding: '10px 24px', background: '#7A5230', color: '#F5F0E8', border: 'none',
          cursor: 'pointer', textTransform: 'uppercase', minHeight: 48 }}>
          RETRY
        </button>
      </div>
    );
  }

  const { revenue = {}, vp_performance = [], engagement_health = [], system_status = {} } = data || {};
  const pendingProspects = prospects.filter(p => p.status === 'pending_approval');

  // Sort engagement health
  const sorted = [...engagement_health].sort((a, b) => {
    const av = a[sortCol] ?? '';
    const bv = b[sortCol] ?? '';
    if (typeof av === 'number' && typeof bv === 'number') return sortAsc ? av - bv : bv - av;
    return sortAsc ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
  });

  function toggleSort(col) {
    if (sortCol === col) setSortAsc(!sortAsc);
    else { setSortCol(col); setSortAsc(true); }
  }

  // Monthly chart max
  const monthlyData = revenue.monthly_breakdown || [];
  const maxRev = Math.max(...monthlyData.map(m => m.revenue), 1);

  return (
    <div style={{ padding: '32px 40px', maxWidth: 1100, background: '#FAFAF8', minHeight: '100vh' }}>
      <style>{`
        .admin-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-bottom: 20px; }
        .admin-system { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 24px; }
        @media (max-width: 768px) {
          .admin-stats { grid-template-columns: 1fr; }
          .admin-system { grid-template-columns: 1fr 1fr; }
        }
        @media (max-width: 480px) {
          .admin-system { grid-template-columns: 1fr; }
        }
      `}</style>
      <div style={{ ...serif, fontSize: 28, fontWeight: 300, color: '#0A1828', marginBottom: 4 }}>Admin</div>
      <div style={{ ...mono, fontSize: 8, color: '#7A5230', letterSpacing: '0.14em', marginBottom: 24 }}>
        BUSINESS INTELLIGENCE
      </div>

      {/* PENDING APPROVALS */}
      {pendingProspects.length > 0 && (
        <>
          <div style={{
            background: 'rgba(184,134,46,0.08)', border: '1px solid rgba(184,134,46,0.25)',
            padding: '14px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <span style={{
              ...mono, fontSize: 12, fontWeight: 600, color: '#B8862E',
              background: 'rgba(184,134,46,0.15)', padding: '4px 10px',
            }}>{pendingProspects.length}</span>
            <span style={{ ...sans, fontSize: 14, color: '#4A6278' }}>
              prospects awaiting your approval
            </span>
          </div>
          <div style={{ ...card, overflowX: 'auto', padding: 0, marginBottom: 32 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th}>Name</th><th style={th}>Company</th><th style={th}>Revenue Band</th>
                  <th style={th}>VP</th><th style={th}>Submitted</th><th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {pendingProspects.map(p => (
                  <tr key={p.id}>
                    <td style={td}>{p.first_name} {p.last_name}</td>
                    <td style={td}>{p.company}</td>
                    <td style={{ ...td, ...mono, fontSize: 11 }}>{p.revenue_band || '--'}</td>
                    <td style={{ ...td, ...mono, fontSize: 11 }}>{p.vp_name || '--'}</td>
                    <td style={{ ...td, ...mono, fontSize: 10, color: 'rgba(10,24,40,0.45)' }}>
                      {p.created_at ? p.created_at.slice(0, 10) : '--'}
                    </td>
                    <td style={td}>
                      <button onClick={() => approveProspect(p.id)} style={{
                        ...mono, fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase',
                        background: '#2A5F3F', color: '#F5F0E8', border: 'none',
                        padding: '8px 16px', cursor: 'pointer', minHeight: 48,
                      }}>Approve</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* SECTION 1: FIRM REVENUE */}
      <div style={{ ...label, marginTop: 32 }}><CL />FIRM REVENUE</div>
      <div className="admin-stats">
        <div style={statCard}>
          <div style={{ ...mono, fontSize: 9, color: 'rgba(10,24,40,0.4)', letterSpacing: '0.12em', marginBottom: 8 }}>
            THIS MONTH
          </div>
          <div style={{ ...sans, fontSize: 28, fontWeight: 500, color: '#0A1828' }}>{fmt(revenue.this_month)}</div>
        </div>
        <div style={statCard}>
          <div style={{ ...mono, fontSize: 9, color: 'rgba(10,24,40,0.4)', letterSpacing: '0.12em', marginBottom: 8 }}>
            THIS QUARTER
          </div>
          <div style={{ ...sans, fontSize: 28, fontWeight: 500, color: '#0A1828' }}>{fmt(revenue.this_quarter)}</div>
        </div>
        <div style={statCard}>
          <div style={{ ...mono, fontSize: 9, color: 'rgba(10,24,40,0.4)', letterSpacing: '0.12em', marginBottom: 8 }}>
            ALL TIME
          </div>
          <div style={{ ...sans, fontSize: 28, fontWeight: 500, color: '#7A5230' }}>{fmt(revenue.all_time)}</div>
        </div>
      </div>

      {/* Monthly Revenue Chart */}
      {monthlyData.length > 0 && (
        <div style={{ ...card, padding: '20px 24px' }}>
          <div style={{ ...mono, fontSize: 7.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#7A94A8', marginBottom: 16 }}>
            MONTHLY REVENUE (LAST 6 MONTHS)
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 120 }}>
            {monthlyData.map((m, i) => {
              const pct = maxRev > 0 ? (m.revenue / maxRev) * 100 : 0;
              return (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                  <div style={{ ...mono, fontSize: 9, color: '#4A6278' }}>
                    {m.revenue > 0 ? `$${Math.round(m.revenue / 1000)}k` : '--'}
                  </div>
                  <div style={{
                    width: '100%', background: '#7A5230', height: Math.max(pct * 0.9, 2),
                    transition: 'height 0.3s ease',
                  }} />
                  <div style={{ ...mono, fontSize: 8, color: '#7A94A8' }}>
                    {m.month ? m.month.slice(5) : ''}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* SECTION 2: VP PERFORMANCE */}
      <div style={{ ...label, marginTop: 32 }}><CL />VP PERFORMANCE</div>
      <div style={{ ...card, overflowX: 'auto', padding: 0 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>VP Name</th>
              <th style={th}>Potential</th>
              <th style={th}>Confirmed</th>
              <th style={th}>Active Engagements</th>
              <th style={th}>Prospects</th>
              <th style={th}>Conversion Rate</th>
              <th style={th}>Last Activity</th>
            </tr>
          </thead>
          <tbody>
            {vp_performance.length === 0 ? (
              <tr><td colSpan={7} style={{ ...td, ...mono, fontSize: 11, color: 'rgba(10,24,40,0.4)', textAlign: 'center' }}>No VP data</td></tr>
            ) : vp_performance.map((vp, i) => (
              <tr key={i}>
                <td style={td}>{vp.name}</td>
                <td style={{ ...td, ...mono, fontSize: 11 }}>{fmt(vp.potential)}</td>
                <td style={{ ...td, ...mono, fontSize: 11, color: vp.confirmed > 0 ? '#2A5F3F' : undefined }}>{fmt(vp.confirmed)}</td>
                <td style={{ ...td, ...mono, fontSize: 11, textAlign: 'center' }}>{vp.active_engagements}</td>
                <td style={{ ...td, ...mono, fontSize: 11, textAlign: 'center' }}>{vp.prospects}</td>
                <td style={{ ...td, ...mono, fontSize: 11 }}>{vp.conversion_rate}%</td>
                <td style={{ ...td, ...mono, fontSize: 10, color: 'rgba(10,24,40,0.45)' }}>
                  {vp.last_activity ? vp.last_activity.replace('T', ' ') : '--'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* SECTION 3: ENGAGEMENT HEALTH */}
      <div style={{ ...label, marginTop: 32 }}><CL />ENGAGEMENT HEALTH</div>
      <div style={{ ...card, overflowX: 'auto', padding: 0 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {[
                { key: 'client_name', label: 'Client' },
                { key: 'company', label: 'Company' },
                { key: 'vp_name', label: 'VP' },
                { key: 'current_stage', label: 'Stage' },
                { key: 'health_score', label: 'Health' },
                { key: 'data_quality', label: 'Data Quality' },
                { key: 'days_in_stage', label: 'Days in Stage' },
                { key: 'last_activity', label: 'Last Activity' },
              ].map(col => (
                <th key={col.key} style={{ ...th, cursor: 'pointer' }} onClick={() => toggleSort(col.key)}>
                  {col.label}{sortCol === col.key ? (sortAsc ? ' \u25B2' : ' \u25BC') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr><td colSpan={8} style={{ ...td, ...mono, fontSize: 11, color: 'rgba(10,24,40,0.4)', textAlign: 'center' }}>No engagements</td></tr>
            ) : sorted.map(e => {
              const days = e.days_in_stage || 0;
              const health = e.health_score ?? 100;
              let borderLeft = 'none';
              if (health < 50) borderLeft = '3px solid rgba(160,40,40,0.85)';
              else if (days > 30) borderLeft = '3px solid rgba(160,40,40,0.85)';
              else if (days > 14) borderLeft = '3px solid #B5832A';

              const dotColor = e.health_grade === 'Excellent' ? '#7A5230'
                : e.health_grade === 'On Track' ? '#0A1828'
                : e.health_grade === 'Needs Attention' ? '#B5832A'
                : e.health_grade === 'At Risk' ? 'rgba(160,40,40,0.85)' : 'rgba(10,24,40,0.2)';

              return (
                <tr key={e.id} style={{ borderLeft }}>
                  <td style={td}>{e.client_name}</td>
                  <td style={td}>{e.company}</td>
                  <td style={{ ...td, ...mono, fontSize: 11 }}>{e.vp_name || '--'}</td>
                  <td style={td}><StatusBadge status={e.status || 'active'} /></td>
                  <td style={{ ...td, ...mono, fontSize: 11 }}>
                    <span style={{ display: 'inline-block', width: 6, height: 6, background: dotColor, marginRight: 6, verticalAlign: 'middle' }} />
                    {health}/100
                  </td>
                  <td style={{ ...td, ...mono, fontSize: 11 }}>{e.data_quality != null ? e.data_quality : '--'}</td>
                  <td style={{ ...td, ...mono, fontSize: 11, color: days > 14 ? '#7A5230' : undefined }}>
                    {days}
                  </td>
                  <td style={{ ...td, ...mono, fontSize: 10, color: 'rgba(10,24,40,0.45)' }}>
                    {e.last_activity ? e.last_activity.replace('T', ' ').slice(0, 16) : '--'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* SECTION 4: SYSTEM STATUS */}
      <div style={{ ...label, marginTop: 32 }}><CL />SYSTEM STATUS</div>
      <div className="admin-system">
        {[
          { key: 'api', name: 'API', detail: 'cordis-workflow.onrender.com' },
          { key: 'database', name: 'Database', detail: 'Supabase connection' },
          { key: 'email', name: 'Email', detail: 'SMTP configured' },
          { key: 'stripe', name: 'Stripe', detail: 'API key configured' },
        ].map(s => {
          const status = system_status[s.key] || 'error';
          const isGood = status === 'ok';
          return (
            <div key={s.key} style={{
              ...card, marginBottom: 0, display: 'flex', alignItems: 'center', gap: 12,
              borderLeft: isGood ? '3px solid #2A5F3F' : '3px solid #7A1F2E',
            }}>
              <span style={{
                width: 8, height: 8, borderRadius: '50%',
                background: isGood ? '#2A5F3F' : status === 'not_configured' ? '#B8862E' : '#7A1F2E',
                flexShrink: 0,
              }} />
              <div>
                <div style={{ ...mono, fontSize: 10, color: '#0A1828', letterSpacing: '0.08em' }}>{s.name}</div>
                <div style={{ ...mono, fontSize: 8, color: '#7A94A8', marginTop: 2 }}>{s.detail}</div>
              </div>
              <div style={{ marginLeft: 'auto' }}>
                <StatusBadge status={status} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
